import { getApiKey, getBootstrapToken, getCognitoToken } from "../lib/auth";

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) || "https://code-insights-dev-api.agentic-ace.io";

async function bReq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getBootstrapToken();
  if (!token) throw new Error("Bootstrap token not set — go to the admin panel to enter it.");
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  if (r.status === 204) return undefined as unknown as T;
  return r.json() as Promise<T>;
}

// Admin Panel requests authenticate with the logged-in user's own session token
// (Cognito ID token or project API key) rather than the shared bootstrap token —
// the backend's /api/v1/admin/* routes check the caller's Cognito group.
async function aReq<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = getApiKey() || getCognitoToken();
  if (!token) throw new Error("Not signed in.");
  const r = await fetch(`${BASE}${path}`, {
    method,
    headers: { "content-type": "application/json", Authorization: `Bearer ${token}` },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!r.ok) throw new Error(`${r.status}: ${await r.text()}`);
  if (r.status === 204) return undefined as unknown as T;
  return r.json() as Promise<T>;
}

export type Org = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  settings: Record<string, unknown> | null;
  created_at: string;
};

export type OrgProject = {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  settings: Record<string, unknown> | null;
  created_at: string;
};

export type ProjectCreated = OrgProject & { api_key: string };

export const orgApi = {
  listOrgs: () => bReq<Org[]>("GET", "/api/v1/organizations"),
  createOrg: (name: string, slug: string, description?: string) =>
    bReq<Org>("POST", "/api/v1/organizations", { name, slug, description }),
  deleteOrg: (orgId: string) => bReq<void>("DELETE", `/api/v1/organizations/${orgId}`),
  listProjects: (orgId: string) =>
    bReq<OrgProject[]>("GET", `/api/v1/organizations/${orgId}/projects`),
  createProject: (orgId: string, name: string, description?: string) =>
    bReq<ProjectCreated>("POST", "/api/v1/projects", {
      name,
      description: description || undefined,
      organization_id: orgId,
    }),
};

export type AdminWhoAmI = { is_admin: boolean; user_id: string | null; email: string | null; via: string };

// Admin Panel — gated server-side by the caller's Cognito group (see routes_admin.py).
export const adminApi = {
  whoami: () => aReq<AdminWhoAmI>("GET", "/api/v1/admin/whoami"),
  listOrgs: () => aReq<Org[]>("GET", "/api/v1/admin/organizations"),
  createOrg: (name: string, slug: string, description?: string) =>
    aReq<Org>("POST", "/api/v1/admin/organizations", { name, slug, description }),
  updateOrgSettings: (orgId: string, settings: Record<string, string>) =>
    aReq<Org>("PATCH", `/api/v1/admin/organizations/${orgId}/settings`, { settings }),
  getAllowedSettingKeys: () => aReq<string[]>("GET", "/api/v1/admin/organizations/settings-keys"),
  deleteOrgSetting: (orgId: string, key: string) =>
    aReq<Org>("DELETE", `/api/v1/admin/organizations/${orgId}/settings/${encodeURIComponent(key)}`),
};
